import { CommentDto } from "../../../../../dtos/CommentDto";
import { CommentType } from "../../../../../dtos/CommentType";
import { DefaultPhaseDto } from "../../../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../../../dtos/DefaultStepDto";
import { FileDto } from "../../../../../dtos/FileDto";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { PhaseStatus } from "../../../../../dtos/PhaseStatus";
import { StepDto } from "../../../../../dtos/StepDto";

export function mapDefaultStepToStepDto(s: DefaultStepDto): StepDto {

    const attachmentNames: string[] = s.settings?.requestFileAttachments ?? [];

    const requestFiles: FileDto[] = attachmentNames.map(name => ({
        id: "",
        name: name,
        description: null,
        creationDate: new Date(),
        uploadDate: new Date(),
        spFileId: null,
        fileSize: null,
        userId: "TEMPLATE",
        user: null,
        ownerName: null,
        grantId: "TEMPLATE",
        phaseId: "TEMPLATE",
        stepId: null,
        commentId: null,
    }));

    return {
        id: String(s.id),
        name: s.name,
        order: s.number,
        description: s.description,
        requestComment: {
            comment: s.description,
            dateUpdated: s.dateUpdated,
            commentType: CommentType.Request,
            files: requestFiles ?? []
        } as CommentDto,
        defaultStep: s
    } as StepDto;
}

// The form edits the template description either directly (None) or as the request text (the
// other types); both start as the saved description, so whichever one changed is what to save.
// An emptied description counts as a change, so it can be cleared.
function editedDescription(s: StepDto): string | null | undefined {
    const saved = s.defaultStep?.description ?? null;
    const request = s.requestComment?.comment ?? null;
    if (s.description !== undefined && (s.description ?? null) !== saved) return s.description;
    return request ?? s.description ?? saved;
}

export function mapStepDtoToDefaultStep(s: StepDto): DefaultStepDto {

    return {
        id: Number(s.id),
        phaseTypeId: s.defaultStep.phaseTypeId,
        name: s.name,
        number: s.order ?? 0,
        description: editedDescription(s),
        instructions: s.defaultStep.instructions,
        abbrev: s.defaultStep.abbrev,
        key: s.defaultStep.key,
        settings: s.defaultStep.settings,
        dateCreated: new Date(),
        dateUpdated: new Date()
    } as DefaultStepDto
}

export function mapDefaultPhaseToPhaseDto(p: DefaultPhaseDto): PhaseDto & { steps: StepDto[] } {

    // Filter out soft-deleted subtasks so they don't show in templates
    const nonDeletedSteps = (p.stepTypes ?? []).filter(s => s && !s.deleted);
    const steps = nonDeletedSteps.map(mapDefaultStepToStepDto);

    return {
        id: String(p.id),
        name: p.name,
        phaseSubmissions: [],
        defaultPhase: p,
        phaseActions: [],
        dateCreated: new Date(),
        dateUpdated: new Date(),
        status: PhaseStatus.READY,
        dueDate: null,
        offset: null,
        description: null,
        startDate: null,
        projectedEndDate: null,
        submissionDate: null,
        completionDate: null,
        submissionOverridden: false,
        completionOverridden: false,
        lastUpdateDate: null,
        owner: null,
        steps
    }
}

export function mapPhaseDtoToDefaultPhase(p: PhaseDto): DefaultPhaseDto {
    return {
        id: Number(p.id),
        moduleTypeId: p.defaultPhase.moduleTypeId,
        name: p.name,
        number: p.defaultPhase.number ?? 0,
        abbrev: p.defaultPhase.abbrev,
        key: p.defaultPhase.key,
        settings: p.defaultPhase.settings,
        stepTypes: p.defaultPhase.stepTypes ?? [],
        deleted: p.defaultPhase.deleted ?? false,
        hasInstances: p.defaultPhase.hasInstances ?? false,
        dateCreated: new Date(p.defaultPhase.dateCreated ?? Date.now()),
        dateUpdated: new Date(),
    };
}
